import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/common/components/Button/Button';
import { TCardActionGroupProps } from '@/common/components/CardActionGroup/CardActionGroup';
import { CardActionUpload } from '@/common/components/CardActionUpload/CardActionUpload';
import { CardGroup } from '@/common/components/CardGroup/CardGroup';
import { Icon } from '@/common/components/Icons/Icon';
import { Stack } from '@/common/components/Stack/Stack';
import { IFile } from '@coverflex-tech/hypernova-utils';
import { BodyText } from '../BodyText/BodyText';

export type TCardActionUploadGroupProps = Omit<
  TCardActionGroupProps,
  'children'
> & {
  cardTitle: string;
  files: IFile[];
  onChange?: (files: IFile[]) => void;
  maxFiles?: number;
  error?: string;
};

export function CardActionUploadGroup({
  files,
  cardTitle,
  onChange,
  maxFiles,
  error,
  ...cardGroupProps
}: TCardActionUploadGroupProps) {
  const { t } = useTranslation();
  const [showEmptyCard, setShowEmptyCard] = useState<boolean>(
    files.length === 0
  );

  const onChangeFiles = (file: IFile | undefined, index: number) => {
    if (!file) {
      const newFiles = files.filter((_, i) => i !== index);
      onChange?.(newFiles);
      if (newFiles.length === 0) {
        setShowEmptyCard(true);
      }
      return;
    }
    const newFiles = [...files];
    newFiles[index] = file;
    onChange?.(newFiles);
  };

  const addCard = () => {
    setShowEmptyCard(true);
  };

  const cards = files.map((file, index) => (
    <CardActionUpload
      key={index}
      file={file}
      title={cardTitle}
      onChange={(file) => onChangeFiles(file, index)}
    />
  ));

  if (showEmptyCard) {
    cards.push(
      <CardActionUpload
        key={files.length}
        title={cardTitle}
        onChange={(file) => {
          onChangeFiles(file, files.length);
          setShowEmptyCard(false);
        }}
      />
    );
  }

  const canAddMore =
    (maxFiles ? files.length < maxFiles : true) &&
    files.length > 0 &&
    !showEmptyCard;

  return (
    <Stack gap="sm">
      <CardGroup gap="2xs" {...cardGroupProps}>
        {cards}
      </CardGroup>
      {error && (
        <BodyText size="sm" color="systemError">
          {error}
        </BodyText>
      )}
      {canAddMore && (
        <Button
          variant="secondary"
          onPress={addCard}
          icon={<Icon icon="plus" />}
        >
          {t('general.fields.file.add')}
        </Button>
      )}
    </Stack>
  );
}
